import { useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardLabel } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTenant } from "@/lib/hooks/useTenant";
import { useEntitlements } from "@/lib/entitlements";
import {
  useCommissions,
  useCommissionsSummary,
  type Commission,
  type OfficialCommissionMarketplace,
} from "@/lib/hooks/useCommissions";
import {
  DollarSign,
  ShoppingBag,
  ShieldCheck,
  Info,
  Lock,
  ReceiptText,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const OFFICIAL_MARKETPLACES: readonly OfficialCommissionMarketplace[] = ["shopee", "awin"];

const MARKETPLACE_CONFIG: Record<string, { label: string; icon: string; hasOfficialCommissions: boolean }> = {
  shopee: { label: "Shopee", icon: "SP", hasOfficialCommissions: true },
  awin: { label: "AWIN", icon: "AW", hasOfficialCommissions: true },
  amazon: { label: "Amazon", icon: "AM", hasOfficialCommissions: false },
  mercadolivre: { label: "Mercado Livre", icon: "ML", hasOfficialCommissions: false },
  aliexpress: { label: "AliExpress", icon: "AE", hasOfficialCommissions: false },
  magalu: { label: "Magalu", icon: "MG", hasOfficialCommissions: false },
  shein: { label: "SHEIN", icon: "SH", hasOfficialCommissions: false },
};

const PERIOD_OPTIONS = [
  { value: "today", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "", label: "Tudo" },
] as const;

const STATUS_LABEL: Record<Commission["status"], string> = {
  pending: "Pendente",
  approved: "Aprovada",
  declined: "Recusada",
  paid: "Paga",
};

const STATUS_VARIANT: Record<Commission["status"], "warning" | "success" | "destructive" | "gold"> = {
  pending: "warning",
  approved: "success",
  declined: "destructive",
  paid: "gold",
};

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function periodToDateFrom(period: string): string | undefined {
  if (!period) return undefined;
  const now = new Date();
  const days = period === "today" ? 1 : period === "7d" ? 7 : 30;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return from.toISOString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(cents: number, currency = "BRL"): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency });
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function PeriodTabs({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.5)] p-0.5">
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            value === opt.value
              ? "bg-[hsl(var(--surface-raised))] text-foreground shadow-sm border border-border"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function MarketplaceIcon({ icon }: { icon: string }): JSX.Element {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-[hsl(var(--surface-raised))] text-xs font-bold text-foreground">
      {icon}
    </div>
  );
}

function MarketplaceSummaryCard({
  code,
  summary,
}: {
  code: string;
  summary: { count: number; totalSaleCents: number; totalCommissionCents: number } | undefined;
}): JSX.Element {
  const cfg = MARKETPLACE_CONFIG[code] ?? { label: code, icon: code.slice(0, 2).toUpperCase(), hasOfficialCommissions: false };

  return (
    <Card variant="raised" className="relative">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <MarketplaceIcon icon={cfg.icon} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{cfg.label}</p>
            {cfg.hasOfficialCommissions ? (
              <Badge variant="gold" className="text-[10px] px-1.5 py-0">
                <ShieldCheck size={10} />
                Comissão oficial
              </Badge>
            ) : (
              <Badge variant="muted" className="text-[10px] px-1.5 py-0">
                Sem integração oficial
              </Badge>
            )}
          </div>
        </div>

        {cfg.hasOfficialCommissions ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <ReceiptText size={11} />
                <span>Transações</span>
              </div>
              <p className="font-semibold text-foreground tabular-nums text-sm">
                {(summary?.count ?? 0).toLocaleString("pt-BR")}
              </p>
            </div>
            <div className="space-y-0.5 text-right">
              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                <DollarSign size={11} />
                <span>Comissão</span>
              </div>
              <p className="font-semibold text-[hsl(var(--gold))] tabular-nums text-sm">
                {formatCurrency(summary?.totalCommissionCents ?? 0)}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed">
            Sem comissão oficial disponível. Este marketplace não possui API de afiliados
            integrada ao LucraLink ainda.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-12 text-center space-y-5">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <ShoppingBag size={24} className="text-primary" />
      </div>
      <div className="space-y-2 max-w-md mx-auto">
        <p className="font-semibold text-foreground">Nenhuma comissão registrada</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          As comissões oficiais da Shopee e da AWIN aparecem aqui automaticamente, sincronizadas
          a cada 6 horas a partir das credenciais configuradas em Minha conta.
        </p>
      </div>
    </div>
  );
}

function LockedState(): JSX.Element {
  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title="Comissões"
        subtitle="Receita consolidada por marketplace e histórico de comissões."
      />
      <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-12 text-center space-y-5">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Lock size={24} className="text-primary" />
        </div>
        <div className="space-y-2 max-w-md mx-auto">
          <p className="font-semibold text-foreground">Comissões é um recurso do plano Pro</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Faça upgrade para o plano Pro ou Pro+IA para acompanhar as comissões oficiais da
            Shopee e da AWIN direto no painel.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComissoesPage
// ---------------------------------------------------------------------------

export function ComissoesPage(): JSX.Element {
  const [period, setPeriod] = useState("7d");
  const [page, setPage] = useState(1);

  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);

  const dateFrom = useMemo(() => periodToDateFrom(period), [period]);
  const { data: summaryRows } = useCommissionsSummary(dateFrom);
  const { data: commissionsPage, isLoading } = useCommissions({
    page,
    pageSize: PAGE_SIZE,
    dateFrom,
  });

  if (!entitlements.hasFullAnalytics) {
    return <LockedState />;
  }

  const summaryByMarketplace = new Map(summaryRows?.map((s) => [s.marketplaceCode, s]));
  const records = commissionsPage?.data ?? [];
  const hasData = records.length > 0;
  const totalCommissionCents = (summaryRows ?? []).reduce((acc, s) => acc + s.totalCommissionCents, 0);
  const totalTransactions = (summaryRows ?? []).reduce((acc, s) => acc + s.count, 0);
  const allMarketplaceCodes = [
    ...OFFICIAL_MARKETPLACES,
    ...Object.keys(MARKETPLACE_CONFIG).filter(
      (code) => !OFFICIAL_MARKETPLACES.includes(code as OfficialCommissionMarketplace),
    ),
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title="Comissões"
        subtitle="Receita consolidada por marketplace e histórico de comissões."
        actions={
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--gold)/0.25)] bg-[hsl(var(--gold)/0.1)] px-3 py-1 text-xs font-medium text-[hsl(var(--gold))]">
              <ShieldCheck size={12} />
              Shopee + AWIN oficiais
            </span>
          </div>
        }
      />

      <PeriodTabs
        value={period}
        onChange={(v) => {
          setPeriod(v);
          setPage(1);
        }}
      />

      <div className="flex items-start gap-3 rounded-lg border border-[hsl(var(--gold)/0.2)] bg-[hsl(var(--gold)/0.04)] px-4 py-3">
        <Info size={16} className="shrink-0 mt-0.5 text-[hsl(var(--gold))]" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Comissões oficiais disponíveis para Shopee e AWIN.</strong>
          {" "}Esses marketplaces possuem API oficial de afiliados sincronizada automaticamente a
          cada 6 horas. Os demais marketplaces ainda não têm integração oficial de comissões.
        </p>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <CardLabel>Resumo por marketplace</CardLabel>
          <span className="text-xs text-muted-foreground tabular-nums font-mono">
            {totalTransactions.toLocaleString("pt-BR")} transações |{" "}
            <span className="text-[hsl(var(--gold))]">{formatCurrency(totalCommissionCents)}</span>
          </span>
        </div>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {allMarketplaceCodes.map((code) => (
            <MarketplaceSummaryCard
              key={code}
              code={code}
              summary={summaryByMarketplace.get(code as OfficialCommissionMarketplace)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <CardLabel>Histórico de comissões</CardLabel>

        {!isLoading && !hasData && <EmptyState />}

        {hasData && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div
              className="grid items-center gap-4 bg-[hsl(var(--surface-raised)/0.7)] px-5 py-3"
              style={{ gridTemplateColumns: "120px 100px 1fr 100px 110px" }}
            >
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Data</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Marketplace</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Produto / Pedido</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Status</span>
              <span className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-widest">Comissão</span>
            </div>

            <div className="divide-y divide-border">
              {records.map((rec) => (
                <div
                  key={rec.id}
                  className={cn(
                    "grid items-center gap-4 px-5 py-3.5",
                    "hover:bg-[hsl(var(--surface-raised)/0.4)] transition-colors",
                  )}
                  style={{ gridTemplateColumns: "120px 100px 1fr 100px 110px" }}
                >
                  <div>
                    <p className="text-xs text-muted-foreground font-mono">{formatDate(rec.transaction_date)}</p>
                  </div>

                  <div>
                    <span className="inline-flex items-center rounded-md border border-border bg-[hsl(var(--surface-raised))] px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {MARKETPLACE_CONFIG[rec.marketplace_code]?.label ?? rec.marketplace_code}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate font-mono text-xs">
                      {rec.product_name ?? rec.order_id ?? rec.external_transaction_id}
                    </p>
                  </div>

                  <div>
                    <Badge variant={STATUS_VARIANT[rec.status]} className="text-[10px] px-1.5 py-0">
                      {STATUS_LABEL[rec.status]}
                    </Badge>
                  </div>

                  <div className="text-right">
                    <p className="text-xs font-semibold text-[hsl(var(--gold))] tabular-nums font-mono">
                      {formatCurrency(rec.commission_amount_cents, rec.currency)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasData && commissionsPage && (
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-muted-foreground">
              Mostrando {records.length} de {commissionsPage.total}{" "}
              {commissionsPage.total === 1 ? "registro" : "registros"}
            </p>
            {commissionsPage.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground disabled:opacity-40"
                >
                  Anterior
                </button>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {page} / {commissionsPage.totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= commissionsPage.totalPages}
                  onClick={() => setPage((p) => Math.min(commissionsPage.totalPages, p + 1))}
                  className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
